import React, { Suspense, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MdArrowBack } from 'react-icons/md';
import PeriodSelectBox from '../../../global/PeriodSelectBox';
import {
    fetchManufacturerProduct,
    fetchManufacturerProductInsights,
    fetchManufacturerProductTransactions
} from '../../../../services/Http/ManufacturerApi';
import { Spinner } from 'reactstrap';
import { useCurrency } from '../../../../context/CurrencyProvider';

const ReactApexChart = React.lazy(() => import('react-apexcharts'));

const ManufacturerProductDetail = () => {
    const { productId } = useParams();
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const navigate = useNavigate();
    const [productData, setProductData] = useState(null);
    const [insights, setInsights] = useState({ sell_in: [], sell_through: [] });
    const [transactions, setTransactions] = useState(null);
    const [period, setPeriod] = useState('last_12_months');
    const [loading, setLoading] = useState(true);
    const [insightLoading, setInsightLoading] = useState(false);
    const formatAmount = useCallback((value, localAmount = null, localCurrencyId = null, localCurrencyCode = null) => {
        return formatResolvedAmount({
            eurAmount: value,
            localAmount,
            localCurrencyId,
            localCurrencyCode,
        });
    }, [formatResolvedAmount]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const data = await fetchManufacturerProduct(productId);
                setProductData(data);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [productId]);

    useEffect(() => {
        const loadInsights = async () => {
            setInsightLoading(true);
            try {
                const [insightsResponse, transactionsResponse] = await Promise.all([
                    fetchManufacturerProductInsights(productId, { period }),
                    fetchManufacturerProductTransactions(productId, { period }),
                ]);
                setInsights(insightsResponse);
                setTransactions(transactionsResponse);
            } finally {
                setInsightLoading(false);
            }
        };

        loadInsights();
    }, [productId, period]);

    if (loading || !productData) {
        return (
            <Centered>
                <Spinner />
            </Centered>
        );
    }

    const { product, top_retailers, top_user_regions, top_retailer_regions, top_buyers } = productData;

    const chartCategories = buildCategories(insights);
    const sellInSeries = chartCategories.map((date) => findQuantity(insights.sell_in, date));
    const sellThroughSeries = chartCategories.map((date) => findQuantity(insights.sell_through, date));

    const summaryConfig = [
        { key: 'sell_in_quantity', label: 'ManufacturerProducts.detail.cards.sellInUnits', formatter: (v) => v.toLocaleString() },
        {
            key: 'sell_in_value',
            label: 'ManufacturerProducts.detail.cards.sellInValue',
            formatter: (v) => formatAmount(v, product.sell_in_value_local, product.sell_in_currency_id, product.sell_in_currency_code),
        },
        { key: 'sell_through_quantity', label: 'ManufacturerProducts.detail.cards.sellThroughUnits', formatter: (v) => v.toLocaleString() },
        {
            key: 'sell_through_value',
            label: 'ManufacturerProducts.detail.cards.sellThroughValue',
            formatter: (v) => formatAmount(v, product.sell_through_value_local, product.sell_through_currency_id, product.sell_through_currency_code),
        },
    ];

    return (
        <Wrapper>
            <Header>
                <button onClick={() => navigate('/products')}>
                    <MdArrowBack /> {t('ManufacturerProducts.detail.back')}
                </button>
                <div>
                    <Title>{product.product_name}</Title>
                    <Subtitle>{product.model_number}</Subtitle>
                </div>
            </Header>

            <SummaryGrid>
                {summaryConfig.map(({ key, label, formatter }) => (
                    <SummaryCard key={key}>
                        <span>{t(label)}</span>
                        <strong>{formatter(product[key] ?? 0)}</strong>
                    </SummaryCard>
                ))}
            </SummaryGrid>

                <Section>
                    <SectionHeader>
                        <h3>{t('ManufacturerProducts.detail.insights')}</h3>
                        <PeriodSelectBox
                            value={period}
                            onChange={(e) => setPeriod(e.target.value)}
                        />
                    </SectionHeader>
                    {insightLoading ? (
                        <Centered><Spinner /></Centered>
                    ) : (
                        <Suspense fallback={<Centered><Spinner /></Centered>}>
                            <ReactApexChart
                                type="area"
                                height={320}
                                series={[
                                    {
                                        name: t('ManufacturerProducts.detail.sellInSeries'),
                                        data: sellInSeries,
                                    },
                                    {
                                        name: t('ManufacturerProducts.detail.sellThroughSeries'),
                                        data: sellThroughSeries,
                                    },
                                ]}
                                options={{
                                    chart: { id: 'product-insights', toolbar: { show: false } },
                                    xaxis: {
                                        categories: chartCategories,
                                    },
                                    colors: ['#821692', '#0DCAF0'],
                                }}
                            />
                        </Suspense>
                    )}
                </Section>

            <InsightGrid>
                <InsightCard>
                    <h4>{t('ManufacturerProducts.detail.topRetailers')}</h4>
                    {renderList(top_retailers, 'retailer', formatAmount)}
                </InsightCard>
                <InsightCard>
                    <h4>{t('ManufacturerProducts.detail.topUserRegions')}</h4>
                    {renderList(top_user_regions, 'region', formatAmount)}
                </InsightCard>
                <InsightCard>
                    <h4>{t('ManufacturerProducts.detail.topRetailerRegions')}</h4>
                    {renderList(top_retailer_regions, 'region', formatAmount)}
                </InsightCard>
                <InsightCard>
                    <h4>{t('ManufacturerProducts.detail.topBuyers')}</h4>
                    {renderList(top_buyers, 'buyer', formatAmount)}
                </InsightCard>
            </InsightGrid>

            <Section>
                <SectionHeader>
                    <h3>{t('ManufacturerProducts.detail.variants')}</h3>
                </SectionHeader>
                {product.variants && product.variants.length > 0 ? (
                    <VariantTable>
                        <thead>
                            <tr>
                                <th>{t('ManufacturerProducts.detail.variantModel')}</th>
                                <th>{t('ManufacturerProducts.detail.variantSku')}</th>
                                <th>{t('ManufacturerProducts.detail.variantAttributes')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {product.variants.map((variant) => (
                                <tr key={variant.id}>
                                    <td>{variant.variant_model_number || '—'}</td>
                                    <td>{variant.sku || '—'}</td>
                                    <td>
                                        {renderAttributes(variant.attributes)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </VariantTable>
                ) : (
                    <Muted>{t('ManufacturerProducts.detail.noVariants')}</Muted>
                )}
            </Section>

            <Section>
                <SectionHeader>
                    <h3>{t('ManufacturerProducts.detail.identifiers')}</h3>
                </SectionHeader>
                {renderIdentifierList(product.variants, t)}
            </Section>

            <Section>
                <SectionHeader>
                    <h3>{t('ManufacturerProducts.detail.transactions')}</h3>
                    <span>{t('ManufacturerProducts.detail.periodNote')}</span>
                </SectionHeader>
                {transactions ? (
                    <TransactionGrid>
                        {Object.entries(transactions).map(([key, value]) => (
                            <TransactionCard key={key}>
                                <span>{t(`ManufacturerProducts.detail.transactions_${key}`)}</span>
                                <strong>{formatAmount(
                                    value.value,
                                    value.value_local,
                                    value.currency_id,
                                    value.currency_code
                                )}</strong>
                                <small>{t('ManufacturerProducts.detail.units', { count: value.quantity })}</small>
                            </TransactionCard>
                        ))}
                    </TransactionGrid>
                ) : (
                    <Muted>{t('ManufacturerProducts.detail.noTransactions')}</Muted>
                )}
            </Section>
        </Wrapper>
    );
};

export default ManufacturerProductDetail;

const buildCategories = (insights) => {
    const dates = new Set();
    (insights.sell_in || []).forEach((row) => dates.add(row.date));
    (insights.sell_through || []).forEach((row) => dates.add(row.date));
    return Array.from(dates).sort();
};

const findQuantity = (rows = [], date) => {
    const match = rows.find((row) => row.date === date);
    return match ? match.quantity : 0;
};

const renderList = (items = [], labelKey, formatAmount) => {
    if (!items || items.length === 0) {
        return <Muted>—</Muted>;
    }

    return (
        <ul>
            {items.map((item, idx) => (
                <li key={`${labelKey}-${idx}`}>
                    <strong>{item[labelKey] || item}</strong>
                    {item.value !== undefined && (
                        <span>{formatAmount(
                            item.value,
                            item.value_local,
                            item.currency_id ?? item.seller_currency_id,
                            item.currency_code ?? item.seller_currency_code
                        )}</span>
                    )}
                    {item.transactions !== undefined && <small>({item.transactions} tx)</small>}
                </li>
            ))}
        </ul>
    );
};

const renderAttributes = (attributes = null) => {
    if (!attributes) {
        return '—';
    }

    const filtered = Object.entries(attributes).filter(([, value]) => value !== null && value !== '');

    if (filtered.length === 0) {
        return '—';
    }

    return filtered.map(([key, value]) => (
        <span key={key} className="badge">
            {key}: {String(value)}
        </span>
    ));
};

const renderIdentifierList = (variants = [], t) => {
    const identifiers = variants.flatMap((variant) =>
        (variant.identifiers || []).map((identifier) => ({
            label: `${identifier.identifier_type}`,
            value: identifier.identifier_value,
            sku: variant.sku,
        }))
    );

    if (identifiers.length === 0) {
        return <Muted>{t('ManufacturerProducts.detail.noIdentifiers')}</Muted>;
    }

    return (
        <VariantTable>
            <thead>
                <tr>
                    <th>{t('ManufacturerProducts.detail.identifierType')}</th>
                    <th>{t('ManufacturerProducts.detail.identifierValue')}</th>
                    <th>{t('ManufacturerProducts.detail.identifierSku')}</th>
                </tr>
            </thead>
            <tbody>
                {identifiers.map((identifier, idx) => (
                    <tr key={`${identifier.label}-${idx}`}>
                        <td>{identifier.label}</td>
                        <td>{identifier.value}</td>
                        <td>{identifier.sku || '—'}</td>
                    </tr>
                ))}
            </tbody>
        </VariantTable>
    );
};

const Wrapper = styled.div`
    background: #fff;
    border-radius: 12px;
    padding: 1.25rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;

    button {
        border: none;
        background: rgba(130, 22, 146, 0.1);
        color: rgba(130, 22, 146, 1);
        padding: 0.5rem 0.875rem;
        border-radius: 8px;
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
        cursor: pointer;
    }
`;

const Title = styled.h2`
    margin: 0;
    color: rgba(130, 22, 146, 1);
`;

const Subtitle = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
`;

const SummaryCard = styled.div`
    background: rgba(130, 22, 146, 0.05);
    border-radius: 12px;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;

    span {
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
    }

    strong {
        font-size: 1.3rem;
        color: ${({ theme }) => theme.colors.textPrimary || '#333'};
    }
`;

const Section = styled.div`
    background: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(130, 22, 146, 0.1);
    border-radius: 12px;
    padding: 1rem;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;

    h3 {
        margin: 0;
        color: rgba(130, 22, 146, 1);
    }
`;

const InsightGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
`;

const InsightCard = styled.div`
    border: 1px solid rgba(130, 22, 146, 0.2);
    border-radius: 12px;
    padding: 1rem;
    background: rgba(130, 22, 146, 0.03);

    h4 {
        margin-top: 0;
        color: rgba(130, 22, 146, 1);
    }

    ul {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 0.375rem;

        li {
            display: flex;
            justify-content: space-between;
            gap: 0.5rem;
            border-bottom: 1px dashed rgba(130, 22, 146, 0.2);
            padding-bottom: 0.25rem;
        }

        small {
            color: ${({ theme }) => theme.colors.textSecondary || '#666'};
        }
    }
`;

const TransactionGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 0.75rem;
`;

const TransactionCard = styled.div`
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 12px;
    padding: 1rem;
    background: rgba(130, 22, 146, 0.02);
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    span {
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
    }

    strong {
        font-size: 1.2rem;
    }
`;

const Centered = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
`;

const Muted = styled.p`
    color: ${({ theme }) => theme.colors.textSecondary || '#777'};
`;

const VariantTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th, td {
        padding: 0.625rem;
        border-bottom: 1px solid rgba(130, 22, 146, 0.1);
    }

    .badge {
        display: inline-block;
        padding: 0.125rem 0.5rem;
        margin: 0.125rem;
        background: rgba(130, 22, 146, 0.15);
        color: ${({ theme }) => theme.colors.textPrimary || '#333'};
        border-radius: 12px;
        font-size: 0.875rem;
        border: 1px solid rgba(130, 22, 146, 0.25);
    }
`;
